import { useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { catalogResponseSchema, formResponseSchema, transferResponseSchema, type IssuanceForm } from '@unit/shared';
import { useApi } from '../../lib/useApi.js';
import { apiPatch, apiPost } from '../../lib/api.js';
import { useAction } from '../../lib/useAction.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useClaimRoles } from '../auth/useClaimRoles.js';
import { ApiError } from '../../components/ApiError.js';
import { ActionError } from '../../components/ActionError.js';
import { Field } from '../../components/Field.js';
import { StatusPill, formatDate } from './FormsPage.js';
import { SignPanel } from '../sign/SignPanel.js';
import { SignatureCanvas } from '../sign/SignatureCanvas.js';
import { PdfButton } from '../../components/PdfButton.js';
import { LineEditor, linesComplete, newLine, toRequestLines, type Line } from './LineEditor.js';
import { IssueErrorDetail } from './IssuePage.js';
import { STOREROOM_ROLES } from '../../components/RequireRole.js';

const secondary =
  'rounded-lg px-3 py-1.5 text-sm text-slate-600 ring-1 ring-slate-300 transition hover:bg-slate-100 disabled:opacity-40';

/**
 * One issuance form, every line in full (REQUIREMENTS §2.6 "form detail").
 *
 * The lines shown are the ones frozen onto the form at issue time — name,
 * serial, quantity, condition — not the live catalog. This is what the
 * soldier will sign for, so it must read the same next year.
 */
export function FormDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { roles } = useClaimRoles();
  const state = useApi(id ? `/forms/${id}` : null, formResponseSchema);

  if (state.kind === 'loading') return <p className="text-slate-500">{t('home.loading')}</p>;
  if (state.kind === 'error') return <ApiError code={state.code} requestId={state.requestId} />;

  const form = state.data.form;
  const isSoldier = user?.uid === form.soldierUid;
  const companyWide = roles.some((r) => STOREROOM_ROLES.includes(r));
  const canCancel = form.status === 'pending_signature' && (form.issuedBy === user?.uid || companyWide);
  // The storeroom corrects an unsigned plain issue (owner, 2026-09-13); a
  // transfer or rotation form carries a pairing the lines belong to.
  const canEdit = form.status === 'pending_signature' && companyWide && form.transferId === null && form.rotationId === null && form.custodianUid === null;
  // Story 25: the storeroom completes a plain issue the soldier cannot sign
  // right now — by attestation under its own signature, never as the soldier.
  // A transfer's override lives on /transfers, where custody moves with it.
  const canAttest = form.status === 'pending_signature' && companyWide && form.transferId === null && form.custodianUid === null;

  return (
    <>
      <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="form-detail" data-status={form.status}>
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 className="text-lg font-semibold text-slate-900">{t('forms.formTitle')}</h2>
          <span className="flex items-center gap-2">
            {(form.status === 'signed' || form.status === 'attested') && (
              <PdfButton path={`/forms/${form.id}/pdf`} filename={`form-${form.id}.pdf`} testId="form-pdf" />
            )}
            <StatusPill status={form.status} />
          </span>
        </div>
        <dl className="mt-3 divide-y divide-slate-100">
          <Field label={t('forms.soldier')}>{form.soldierName}</Field>
          <Field label={t('forms.issuer')}>{form.issuerName}</Field>
          <Field label={t('forms.issuedAt')} latin>{formatDate(form.issuedAt, i18n.language)}</Field>
          {form.dueDate && <Field label={t('forms.dueDate')} latin>{formatDate(form.dueDate, i18n.language)}</Field>}
          {form.soldierComment && <Field label={t('forms.soldierComment')}>{form.soldierComment}</Field>}
          {form.rejectedReason && <Field label={t('forms.reason')}>{form.rejectedReason}</Field>}
        </dl>

        <h3 className="mt-5 font-semibold text-slate-900">{t('forms.lines', { count: form.lineItems.length })}</h3>
        <ul className="mt-2 divide-y divide-slate-100" data-testid="form-lines">
          {form.lineItems.map((line, i) => (
            <li key={i} className="flex flex-wrap items-center justify-between gap-2 py-2" data-testid="form-line">
              <span>
                <span className="font-medium text-slate-900">
                  {i18n.language.startsWith('en') ? line.nameEn : line.nameHe}
                </span>
                {line.serialNumber && (
                  <span className="latin-inline ms-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">
                    {line.serialNumber}
                  </span>
                )}
                {!line.serialNumber && (
                  <span className="ms-2 text-sm text-slate-500">
                    × <span className="latin-inline">{line.quantity}</span>
                  </span>
                )}
              </span>
              <span className="text-xs text-slate-500">
                {t(`inventory.condition.${line.condition}`)}
                {line.notes && ` · ${line.notes}`}
              </span>
            </li>
          ))}
        </ul>

        {form.transferId && <TransferNote key={form.status} transferId={form.transferId} />}
        {form.custodianUid && (
          <p className="mt-4 rounded-xl bg-sky-50 p-3 text-sm text-sky-900" data-testid="distribution-note">
            {t('forms.distributionNote', { sergeant: form.issuerName })}
          </p>
        )}
        {form.status === 'attested' && (
          <div className="mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-900" data-testid="attested-note">
            <p>{t('forms.attestedNote')}</p>
            {form.attestation && (
              <p className="mt-1 font-medium" data-testid="attested-by">
                {t('forms.attestedBy', { by: form.attestation.byName, comment: form.attestation.comment })}
              </p>
            )}
          </div>
        )}
        {form.editedAt && (
          <p className="mt-2 text-xs text-slate-500" data-testid="edited-note">
            {t('forms.editedAt', { date: formatDate(form.editedAt, i18n.language) })}
          </p>
        )}
        {form.status === 'signed' && form.signedAt && (
          <p className="mt-4 rounded-xl bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="signed-note">
            {t('forms.signedAt', { date: formatDate(form.signedAt, i18n.language) })}
          </p>
        )}
      </section>

      {isSoldier && form.status === 'pending_signature' && (
        <SignPanel form={form} onChanged={state.reload} />
      )}

      {canEdit && <EditLinesCard form={form} onDone={state.reload} />}

      {canAttest && <AttestCard form={form} onDone={state.reload} />}

      {canCancel && <CancelCard form={form} onDone={state.reload} />}

      <Link to="/forms" className="text-sm text-slate-500 underline-offset-4 hover:underline">
        {t('forms.backToList')}
      </Link>
    </>
  );
}

/**
 * A transfer form says where the transfer stands (owner, 2026-09-21): the
 * receiver's signature alone does not move the item; the sign-off does.
 * Keyed on the form's status by the caller, so a signature just made
 * fetches the transfer again rather than showing the state before it.
 */
function TransferNote({ transferId }: { transferId: string }) {
  const { t } = useTranslation();
  const state = useApi(`/transfers/${transferId}`, transferResponseSchema);
  const status = state.kind === 'ready' ? state.data.transfer.status : null;
  return (
    <div className="mt-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-900" data-testid="transfer-note" data-transfer-status={status ?? ''}>
      <p>{t('forms.transferNote')}</p>
      {status && (
        <p className="mt-1 font-medium" data-testid="transfer-state">
          {t(`forms.transferState.${status}`)}{' '}
          <Link to={`/transfers?status=${status}`} className="font-normal underline-offset-4 hover:underline">
            {t('forms.openTransfer')}
          </Link>
        </p>
      )}
    </div>
  );
}

/**
 * The attestation (owner, 2026-09-13). The canvas is the admin's or
 * csm's *own* signature, sent with a mandatory reason; the server
 * stores it under `attestations/`, never as the soldier's signature, and the
 * form goes `attested`, never `signed`.
 */
function AttestCard({ form, onDone }: { form: IssuanceForm; onDone: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [image, setImage] = useState<string | null>(null);
  const onInk = useCallback((dataUrl: string | null) => setImage(dataUrl), []);
  const act = useAction(() => apiPost(`/forms/${form.id}/attest`, formResponseSchema, { comment: comment.trim(), imageDataUrl: image }));

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-red-200" data-testid="attest-card">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="font-semibold text-slate-900">{t('forms.attestTitle')}</h3>
        {!open && (
          <button type="button" onClick={() => setOpen(true)} className={secondary} data-testid="attest-open">
            {t('forms.attestOpen')}
          </button>
        )}
      </div>
      <p className="mt-1 text-sm text-slate-500">{t('forms.attestHint')}</p>
      {open && (
        <div className="mt-3 rounded-xl bg-red-50 p-3 ring-1 ring-red-200" data-testid="attest-panel">
          <label className="block text-sm text-slate-700">
            {t('forms.attestComment')}
            <textarea
              className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
              rows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              data-testid="attest-comment"
            />
          </label>
          <p className="mt-2 text-sm text-slate-700">{t('forms.attestSignature')}</p>
          <div className="mt-1">
            <SignatureCanvas onChange={onInk} disabled={act.busy} />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void act.run().then((r) => r && onDone())}
              disabled={act.busy || comment.trim().length < 3 || !image}
              className="rounded-lg bg-red-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-800 disabled:opacity-40"
              data-testid="attest-submit"
            >
              {t('forms.attestSubmit')}
            </button>
            <button type="button" onClick={() => setOpen(false)} className={secondary}>
              {t('org.cancel')}
            </button>
          </div>
          <div className="mt-2"><ActionError error={act.error} /></div>
        </div>
      )}
    </section>
  );
}

function CancelCard({ form, onDone }: { form: IssuanceForm; onDone: () => void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const cancel = useAction(() => apiPost(`/forms/${form.id}/cancel`, formResponseSchema, { reason }));

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="cancel-card">
      <h3 className="font-semibold text-slate-900">{t('forms.cancelTitle')}</h3>
      <p className="mt-1 text-sm text-slate-500">{t('forms.cancelHint')}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input
          className="grow rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
          placeholder={t('forms.reason')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="cancel-reason"
        />
        <button
          type="button"
          onClick={() => void cancel.run().then((r) => r && onDone())}
          disabled={cancel.busy || reason.trim().length < 3}
          className={secondary}
          data-testid="cancel-submit"
        >
          {t('forms.cancel')}
        </button>
      </div>
      <div className="mt-2"><ActionError error={cancel.error} /></div>
    </section>
  );
}

/**
 * The storeroom's edit of an unsigned form: the current lines, editable, and
 * one save that releases and re-reserves in a single server transaction.
 */
function EditLinesCard({ form, onDone }: { form: IssuanceForm; onDone: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [saved, setSaved] = useState(false);
  const catalog = useApi(open ? '/catalog' : null, catalogResponseSchema);
  const items = useMemo(
    () => (catalog.kind === 'ready' ? catalog.data.items.filter((i) => i.active) : []),
    [catalog],
  );
  // The form's frozen lines, turned back into editor lines once the catalog is here.
  const initial = useMemo<Line[] | null>(() => {
    if (catalog.kind !== 'ready') return null;
    const byId = new Map(catalog.data.items.map((i) => [i.id, i]));
    const lines: Line[] = [];
    for (const l of form.lineItems) {
      const item = byId.get(l.catalogItemId);
      if (!item) continue;
      lines.push(
        newLine(item, {
          unit: l.inventoryUnitId && l.serialNumber ? { id: l.inventoryUnitId, serialNumber: l.serialNumber } : null,
          quantity: l.quantity,
          condition: l.condition,
          notes: l.notes ?? '',
        }),
      );
    }
    return lines;
  }, [catalog, form.lineItems]);
  const [lines, setLines] = useState<Line[] | null>(null);
  const current = lines ?? initial;
  const save = useAction(() =>
    apiPatch(`/forms/${form.id}/lines`, formResponseSchema, { lines: toRequestLines(current ?? []), reason: reason.trim() || null }),
  );

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="edit-lines">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="font-semibold text-slate-900">{t('forms.editTitle')}</h3>
        {!open && (
          <button type="button" onClick={() => { setOpen(true); setSaved(false); }} className={secondary} data-testid="edit-lines-open">
            {t('forms.edit')}
          </button>
        )}
      </div>
      <p className="mt-1 text-sm text-slate-500">{t('forms.editHint')}</p>
      {saved && <p className="mt-2 text-sm text-emerald-700" data-testid="edit-lines-saved">{t('forms.edited')}</p>}
      {open && current && (
        <>
          <LineEditor lines={current} onChange={setLines} items={items} testId="edit" />
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input
              className="grow rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
              placeholder={t('forms.editReason')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="edit-lines-reason"
            />
            <button
              type="button"
              onClick={() =>
                void save.run().then((r) => {
                  if (r) {
                    setOpen(false);
                    setLines(null);
                    setSaved(true);
                    onDone();
                  }
                })
              }
              disabled={save.busy || !linesComplete(current)}
              className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-40"
              data-testid="edit-lines-submit"
            >
              {t('forms.editSubmit')}
            </button>
            <button type="button" onClick={() => { setOpen(false); setLines(null); }} className={secondary}>
              {t('org.cancel')}
            </button>
          </div>
          <div className="mt-2">
            <ActionError error={save.error} />
            <IssueErrorDetail error={save.error} />
          </div>
        </>
      )}
    </section>
  );
}
